import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useConfigurators from "../../api/configurator";
import useItems from "../../api/items";
import { useTheme } from "../../context/ThemeContext";
import MollieService from "../../api/mollie";
import { getAuthToken } from "../../context/AuthContext";
import { smartCompressImage } from "../../lib/imageCompression";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  SlidersHorizontal,
  Plus,
  Save,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Eye,
  Settings,
  Layers,
  X,
  Upload,
  Check,
  RefreshCw,
  FolderOpen,
} from "lucide-react";

// Extracted sub-components
import {
  getImageUrl,
  generateId,
  DEFAULT_SETTINGS,
  STEP_TYPES,
  MAX_ZONES,
} from "./configurator/configuratorConstants";
import StepRow from "./configurator/StepRow";
import StepDialog from "./configurator/StepDialog";
import OptionDialog from "./configurator/OptionDialog";
import SettingsDialog from "./configurator/SettingsDialog";
import ConfiguratorPreview from "./configurator/ConfiguratorPreview";

const ConfiguratorEditor = () => {
  const { configuratorId, companyId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const {
    getConfiguratorById,
    createConfigurator,
    updateConfigurator,
    getConfiguratorByIdForCompany,
    createConfiguratorForCompany,
    updateConfiguratorForCompany,
  } = useConfigurators();
  const { uploadImage } = useItems();

  const isNew = configuratorId === "new";
  const isAdmin = !!companyId;

  // Refs for hidden file inputs
  const coverImageInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Main state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [status, setStatus] = useState("draft");
  const [steps, setSteps] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  // UI state
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [showStepDialog, setShowStepDialog] = useState(false);
  const [showOptionDialog, setShowOptionDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [editingOptionStepId, setEditingOptionStepId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Auto-save (debounced, only for existing configurators)
  const autoSaveTimer = useRef(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Don't auto-save before data has loaded or for new configurators
    if (isNew || loading || !hasLoadedRef.current) return;
    if (!name.trim()) return;

    // Clear previous timer
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

    autoSaveTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        const payload = {
          name,
          description,
          image,
          steps,
          settings,
          status,
        };
        if (isAdmin) {
          await updateConfiguratorForCompany(
            companyId,
            configuratorId,
            payload,
          );
        } else {
          await updateConfigurator(configuratorId, payload);
        }
        setSuccessMessage("Automatisch opgeslagen");
        setTimeout(() => setSuccessMessage(""), 2000);
      } catch (error) {
        console.error("Auto-save failed:", error);
      } finally {
        setSaving(false);
      }
    }, 3000);

    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [name, description, image, steps, settings]);

  // Build grouped step segments for the step list
  const stepSegments = useMemo(() => {
    const segments = [];
    let currentGroup = null;
    steps.forEach((step, index) => {
      const g = step.group?.trim() || null;
      if (g) {
        if (currentGroup && currentGroup.name === g) {
          currentGroup.items.push({ step, index });
        } else {
          currentGroup = { type: "group", name: g, items: [{ step, index }] };
          segments.push(currentGroup);
        }
      } else {
        currentGroup = null;
        segments.push({ type: "step", step, index });
      }
    });
    return segments;
  }, [steps]);

  // Auto-expand all groups on initial load
  useEffect(() => {
    const groups = stepSegments
      .filter((s) => s.type === "group")
      .map((s) => s.name);
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      groups.forEach((g) => {
        if (!next.has(g)) next.add(g);
      });
      return next;
    });
  }, [stepSegments]);

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  // Step form state
  const [stepForm, setStepForm] = useState({
    name: "",
    description: "",
    type: "select",
    required: true,
    group: "",
    imageMode: "replace",
    hiddenLayer: false,
    tintWithColor: false,
    svgContent: "",
    zones: [],
  });

  // Option form state
  const [optionForm, setOptionForm] = useState({
    name: "",
    description: "",
    image: "",
    images: [],
    price: 0,
    priceType: "addition",
    colorHex: "",
    colorHex2: "",
    gradientAngle: 90,
    inStock: true,
    keepOriginalSize: false,
    dependencies: [],
    svgContent: "",
    zones: [],
  });

  // Load existing configurator
  useEffect(() => {
    if (!isNew) {
      loadConfigurator();
    }
  }, [configuratorId]);

  // Auto-fill Mollie profile ID from connected account
  useEffect(() => {
    const fillMollieProfileId = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const info = await MollieService.getInfo(token);
        if (info?.profiles?.length > 0) {
          const profileId = info.profiles[0].id;
          setSettings((prev) => ({
            ...prev,
            checkoutOptions: {
              ...prev.checkoutOptions,
              mollieProfileId:
                prev.checkoutOptions?.mollieProfileId || profileId,
            },
          }));
        }
      } catch {
        // No Mollie connection, keep field empty
      }
    };
    fillMollieProfileId();
  }, []);

  const loadConfigurator = async () => {
    try {
      setLoading(true);
      const data = isAdmin
        ? await getConfiguratorByIdForCompany(companyId, configuratorId)
        : await getConfiguratorById(configuratorId);
      if (data) {
        setName(data.name || "");
        setDescription(data.description || "");
        setImage(data.image || "");
        setStatus(data.status || "draft");
        setSteps(data.steps || []);
        setSettings({ ...DEFAULT_SETTINGS, ...(data.settings || {}) });
      }
    } catch (error) {
      console.error("Error loading configurator:", error);
    } finally {
      setLoading(false);
      // Mark as loaded so auto-save can start watching for changes
      setTimeout(() => {
        hasLoadedRef.current = true;
      }, 500);
    }
  };

  // Save configurator
  const handleSave = async (newStatus = null) => {
    if (!name.trim()) {
      alert("Geef je configurator een naam.");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name,
        description,
        image,
        steps,
        settings,
        status: newStatus || status,
      };

      if (isNew) {
        const result = isAdmin
          ? await createConfiguratorForCompany(companyId, payload)
          : await createConfigurator(payload);
        setSuccessMessage("Configurator aangemaakt!");
        setTimeout(() => {
          navigate(
            isAdmin
              ? `/dashboard/admin-configurators/${companyId}/${result.id}`
              : `/dashboard/configurator/${result.id}`,
          );
        }, 500);
      } else {
        if (isAdmin) {
          await updateConfiguratorForCompany(
            companyId,
            configuratorId,
            payload,
          );
        } else {
          await updateConfigurator(configuratorId, payload);
        }
        if (newStatus) setStatus(newStatus);
        setSuccessMessage("Wijzigingen opgeslagen!");
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (error) {
      console.error("Error saving configurator:", error);
      alert("Er ging iets mis bij het opslaan. Probeer opnieuw.");
    } finally {
      setSaving(false);
    }
  };

  // â”€â”€â”€ STEP MANAGEMENT â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const openAddStep = () => {
    setEditingStep(null);
    setStepForm({
      name: "",
      description: "",
      type: "select",
      required: true,
      group: "",
      imageMode: "replace",
      hiddenLayer: false,
      tintWithColor: false,
      svgContent: "",
      zones: [],
      frameImage: "",
      frameImageDefaultColor: "#ffffff",
      frameImageDefaultColor2: "",
      frameImageDefaultGradientAngle: 90,
    });
    setShowStepDialog(true);
  };

  const openEditStep = (step) => {
    setEditingStep(step);
    setStepForm({
      name: step.name,
      description: step.description || "",
      type: step.type || "select",
      required: step.required !== false,
      group: step.group || "",
      imageMode: step.imageMode || "replace",
      hiddenLayer: step.hiddenLayer || false,
      tintWithColor: step.tintWithColor || false,
      svgContent: step.svgContent || "",
      zones: step.zones || [],
      frameImage: step.frameImage || "",
      frameImageDefaultColor: step.frameImageDefaultColor || "#ffffff",
      frameImageDefaultColor2: step.frameImageDefaultColor2 || "",
      frameImageDefaultGradientAngle: step.frameImageDefaultGradientAngle ?? 90,
    });
    setShowStepDialog(true);
  };

  const handleSaveStep = () => {
    if (!stepForm.name.trim()) return;

    if (editingStep) {
      // Update existing step
      setSteps((prev) =>
        prev.map((s) => (s.id === editingStep.id ? { ...s, ...stepForm } : s)),
      );
    } else {
      // Add new step
      const newStep = {
        id: generateId(),
        ...stepForm,
        options: [],
        sortOrder: steps.length,
      };
      setSteps((prev) => [...prev, newStep]);
      setActiveStepIndex(steps.length);
    }
    setShowStepDialog(false);
  };

  const handleDeleteStep = (stepId) => {
    if (
      window.confirm(
        "Weet je zeker dat je deze stap wilt verwijderen? Alle opties worden ook verwijderd.",
      )
    ) {
      setSteps((prev) => prev.filter((s) => s.id !== stepId));
      setActiveStepIndex(null);
    }
  };

  const moveStep = (index, direction) => {
    const newSteps = [...steps];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newSteps.length) return;
    [newSteps[index], newSteps[newIndex]] = [
      newSteps[newIndex],
      newSteps[index],
    ];
    setSteps(newSteps);
    setActiveStepIndex(newIndex);
  };

  const duplicateStep = (step) => {
    const duplicate = {
      ...JSON.parse(JSON.stringify(step)),
      id: generateId(),
      name: `${step.name} (kopie)`,
      options: step.options.map((opt) => ({ ...opt, id: generateId() })),
    };
    setSteps((prev) => [...prev, duplicate]);
  };

  // â”€â”€â”€ OPTION MANAGEMENT â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const openAddOption = (stepId) => {
    const step = steps.find((s) => s.id === stepId);
    const isFC = step?.type === "frame-color";
    setEditingOption(null);
    setEditingOptionStepId(stepId);
    setOptionForm({
      name: "",
      description: "",
      image: "",
      images: [],
      price: 0,
      priceType: "addition",
      colorHex: isFC ? "#ffffff" : "",
      colorHex2: "",
      gradientAngle: 90,
      inStock: true,
      keepOriginalSize: false,
      dependencies: [],
      svgContent: "",
      zones: [],
    });
    setShowOptionDialog(true);
  };

  const openEditOption = (stepId, option) => {
    setEditingOption(option);
    setEditingOptionStepId(stepId);

    // Clean up dependencies: remove stale optionIds that no longer exist in their step
    const cleanedDeps = (option.dependencies || []).map((dep) => {
      const depStep = steps.find((s) => s.id === dep.stepId);
      if (!depStep) return dep;
      const validIds = (dep.optionIds || []).filter((id) =>
        depStep.options.some((o) => o.id === id),
      );
      return { ...dep, optionIds: validIds };
    });

    setOptionForm({
      name: option.name || "",
      description: option.description || "",
      image: option.image || "",
      images: option.images || [],
      price: option.price || 0,
      priceType: option.priceType || "addition",
      colorHex: option.colorHex || "",
      colorHex2: option.colorHex2 || "",
      gradientAngle: option.gradientAngle ?? 90,
      inStock: option.inStock !== false,
      keepOriginalSize: !!option.keepOriginalSize,
      dependencies: cleanedDeps,
      svgContent: option.svgContent || "",
      zones: option.zones || [],
    });
    setShowOptionDialog(true);
  };

  const handleSaveOption = () => {
    if (!optionForm.name.trim()) return;

    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== editingOptionStepId) return step;

        if (editingOption) {
          // Update existing option
          return {
            ...step,
            options: step.options.map((opt) =>
              opt.id === editingOption.id ? { ...opt, ...optionForm } : opt,
            ),
          };
        } else {
          // Add new option
          return {
            ...step,
            options: [
              ...step.options,
              {
                id: generateId(),
                ...optionForm,
                sortOrder: step.options.length,
              },
            ],
          };
        }
      }),
    );
    setShowOptionDialog(false);
  };

  const handleDeleteOption = (stepId, optionId) => {
    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== stepId) return step;
        return {
          ...step,
          options: step.options.filter((opt) => opt.id !== optionId),
        };
      }),
    );
  };

  const moveOption = (stepId, index, direction) => {
    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== stepId) return step;
        const newOptions = [...step.options];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= newOptions.length) return step;
        [newOptions[index], newOptions[newIndex]] = [
          newOptions[newIndex],
          newOptions[index],
        ];
        return { ...step, options: newOptions };
      }),
    );
  };

  const setDefaultOption = (stepId, optionId) => {
    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== stepId) return step;
        // Toggle: if already default, remove it; otherwise set it
        return {
          ...step,
          defaultOptionId: step.defaultOptionId === optionId ? null : optionId,
        };
      }),
    );
  };

  const duplicateOption = (stepId, option) => {
    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== stepId) return step;
        const duplicate = {
          ...JSON.parse(JSON.stringify(option)),
          id: generateId(),
          name: `${option.name} (kopie)`,
        };
        return { ...step, options: [...step.options, duplicate] };
      }),
    );
  };

  // â”€â”€â”€ IMAGE HANDLING (S3 Upload) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const compressedDataUrl = await smartCompressImage(file);
      const resp = await uploadImage(compressedDataUrl);
      const imageKey = resp?.data?.imageKey || resp?.data?.imageId;
      if (imageKey) {
        setImage(imageKey);
      }
    } catch (error) {
      console.error("Cover image upload failed:", error);
      alert("Afbeelding uploaden mislukt");
    } finally {
      setUploading(false);
      if (coverImageInputRef.current) coverImageInputRef.current.value = "";
    }
  };

  const handleOptionMainImageUpload = async (e, ref) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const compressedDataUrl = await smartCompressImage(file);
      const resp = await uploadImage(compressedDataUrl);
      const imageKey = resp?.data?.imageKey || resp?.data?.imageId;
      if (imageKey) {
        setOptionForm((prev) => ({ ...prev, image: imageKey }));
      }
    } catch (error) {
      console.error("Option image upload failed:", error);
      alert("Afbeelding uploaden mislukt");
    } finally {
      setUploading(false);
      if (ref?.current) ref.current.value = "";
    }
  };

  const handleOptionExtraImageUpload = async (e, ref) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const compressedDataUrl = await smartCompressImage(file);
        const resp = await uploadImage(compressedDataUrl);
        const imageKey = resp?.data?.imageKey || resp?.data?.imageId;
        if (imageKey) {
          setOptionForm((prev) => ({
            ...prev,
            images: [...prev.images, imageKey],
          }));
          // If no main image yet, set the first uploaded as main
          setOptionForm((prev) => {
            if (!prev.image) return { ...prev, image: imageKey };
            return prev;
          });
        }
      }
    } catch (error) {
      console.error("Extra image upload failed:", error);
      alert("Afbeelding uploaden mislukt");
    } finally {
      setUploading(false);
      if (ref?.current) ref.current.value = "";
    }
  };

  const removeImage = (index) => {
    setOptionForm((prev) => {
      const newImages = prev.images.filter((_, i) => i !== index);
      return {
        ...prev,
        images: newImages,
        image: index === 0 && newImages.length > 0 ? newImages[0] : prev.image,
      };
    });
  };

  // â”€â”€â”€ SVG FRAME HANDLING â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const parseSvgZones = (svgContent) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgContent, "image/svg+xml");
      const zones = [];
      const skipTags = new Set([
        "svg",
        "defs",
        "clippath",
        "mask",
        "symbol",
        "metadata",
        "title",
        "desc",
        "style",
        "lineargradient",
        "radialgradient",
        "stop",
        "filter",
        "fegaussianblur",
        "feoffset",
        "feblend",
        "fecolormatrix",
        "fecomposite",
        "feflood",
        "femerge",
        "femergenode",
        "use",
        "text",
        "tspan",
      ]);
      const visualTags = new Set([
        "path",
        "rect",
        "circle",
        "ellipse",
        "polygon",
        "polyline",
        "line",
        "g",
      ]);
      const seenIds = new Set();

      // Pass 1: pick up elements that already have an id (max MAX_ZONES)
      doc.querySelectorAll("[id]").forEach((el) => {
        if (zones.length >= MAX_ZONES) return;
        const tagName = el.tagName.toLowerCase();
        if (skipTags.has(tagName)) return;
        const id = el.getAttribute("id");
        if (seenIds.has(id)) return;
        seenIds.add(id);
        const fill = el.getAttribute("fill");
        zones.push({
          id: generateId(),
          elementId: id,
          name: id
            .replace(/[-_]/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase()),
          defaultColor: fill && fill !== "none" ? fill : "#333333",
          enabled: true,
        });
      });

      // Pass 2: if no zones found via IDs, group visual elements by fill color
      // and create one zone per colour group (max MAX_ZONES biggest groups)
      if (zones.length === 0) {
        const svgEl = doc.querySelector("svg");
        if (svgEl) {
          // Collect visual elements and group by fill colour
          const colorGroups = new Map(); // fill -> [elements]
          svgEl.querySelectorAll("*").forEach((el) => {
            const tagName = el.tagName.toLowerCase();
            if (!visualTags.has(tagName)) return;
            if (tagName === "g") return; // skip groups, only direct shapes
            const fill = (
              el.getAttribute("fill") ||
              el.style?.fill ||
              ""
            ).toLowerCase();
            if (!fill || fill === "none" || fill === "transparent") return;
            if (!colorGroups.has(fill)) colorGroups.set(fill, []);
            colorGroups.get(fill).push(el);
          });

          // Sort groups by number of elements (biggest first), take top MAX_ZONES
          const sortedGroups = [...colorGroups.entries()]
            .sort((a, b) => b[1].length - a[1].length)
            .slice(0, MAX_ZONES);

          let counter = 1;
          sortedGroups.forEach(([fill, elements]) => {
            // Wrap elements of this colour group in a <g> with an auto-id
            // so the colour picker can target them all at once
            const autoId = `zone-${counter}`;
            // Give the first element the id; also tag all siblings so colorizer catches them
            if (elements.length === 1) {
              elements[0].setAttribute("id", autoId);
            } else {
              // Create wrapper <g>
              const g = doc.createElementNS("http://www.w3.org/2000/svg", "g");
              g.setAttribute("id", autoId);
              g.setAttribute("fill", fill);
              const parent = elements[0].parentNode;
              parent.insertBefore(g, elements[0]);
              elements.forEach((el) => {
                el.removeAttribute("fill"); // inherit from group
                g.appendChild(el);
              });
            }
            zones.push({
              id: generateId(),
              elementId: autoId,
              name: `Zone ${counter}`,
              defaultColor: fill,
              enabled: true,
            });
            counter++;
          });

          if (zones.length > 0) {
            return {
              zones,
              updatedSvg: new XMLSerializer().serializeToString(doc),
            };
          }
        }
      }

      return { zones, updatedSvg: null };
    } catch (e) {
      console.error("SVG parsing error:", e);
      return { zones: [], updatedSvg: null };
    }
  };

  // â”€â”€â”€ OPTION SVG UPLOAD (for frame-color options) â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleOptionSvgUpload = (e, ref) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".svg") && file.type !== "image/svg+xml") {
      alert("Upload een SVG bestand (.svg)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const svgContent = event.target.result;
      const { zones, updatedSvg } = parseSvgZones(svgContent);
      setOptionForm((p) => ({
        ...p,
        svgContent: updatedSvg || svgContent,
        zones,
        // Clear image when using SVG mode
        image: "",
      }));
    };
    reader.readAsText(file);
    if (ref?.current) ref.current.value = "";
  };

  // â”€â”€â”€ DEPENDENCY MANAGEMENT â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const getAvailableDependencySteps = (currentStepId) => {
    const currentIndex = steps.findIndex((s) => s.id === currentStepId);
    return steps.slice(0, currentIndex);
  };

  const addDependency = () => {
    setOptionForm((prev) => ({
      ...prev,
      dependencies: [...prev.dependencies, { stepId: "", optionIds: [] }],
    }));
  };

  const updateDependency = (index, field, value) => {
    setOptionForm((prev) => ({
      ...prev,
      dependencies: prev.dependencies.map((dep, i) =>
        i === index
          ? {
              ...dep,
              [field]: value,
              // Clear optionIds when changing step (old IDs are from a different step)
              ...(field === "stepId" && value !== dep.stepId
                ? { optionIds: [] }
                : {}),
            }
          : dep,
      ),
    }));
  };

  const removeDependency = (index) => {
    setOptionForm((prev) => ({
      ...prev,
      dependencies: prev.dependencies.filter((_, i) => i !== index),
    }));
  };

  // â”€â”€â”€ RENDER â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
        ></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-20">
      {/* Success toast */}
      {successMessage && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-in fade-in slide-in-from-top-2 ${isDark ? "bg-green-900/20 border border-green-500/30 text-green-400" : "bg-green-50 border border-green-200 text-green-700"}`}
        >
          <Check className="h-4 w-4" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate(
                isAdmin
                  ? "/dashboard/admin-configurators"
                  : "/dashboard/configurator",
              )
            }
            className={
              isDark
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1
              className={`text-2xl font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {isNew ? "Nieuwe Configurator" : "Configurator Bewerken"}
            </h1>
            <p className={`mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {isNew
                ? "Bouw een interactieve product configurator"
                : `Bewerk "${name}"`}
            </p>
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className={`text-sm sm:text-base ${
              isDark
                ? "border-white/[0.06] text-gray-300 hover:bg-white/[0.04]"
                : "border-gray-200"
            }`}
          >
            <Eye className="mr-1 sm:mr-2 h-4 w-4" />
            {showPreview ? "Editor" : "Preview"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowSettingsDialog(true)}
            className={`text-sm sm:text-base ${
              isDark
                ? "border-white/[0.06] text-gray-300 hover:bg-white/[0.04]"
                : "border-gray-200"
            }`}
          >
            <Settings className="mr-1 sm:mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Instellingen</span>
            <span className="sm:hidden">Instell.</span>
          </Button>
          {!isNew && status === "draft" && (
            <Button
              onClick={() => handleSave("published")}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Publiceren
            </Button>
          )}
          {!isNew && status === "published" && (
            <Button
              variant="outline"
              onClick={() => handleSave("draft")}
              disabled={saving}
              className={
                isDark
                  ? "border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  : "border-amber-300 text-amber-700 hover:bg-amber-50"
              }
            >
              Depubliceren
            </Button>
          )}
          <Button
            onClick={() => handleSave()}
            disabled={saving}
            className={
              isDark
                ? "bg-white text-black hover:bg-gray-200"
                : "bg-gray-900 text-white hover:bg-gray-800"
            }
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Opslaan..." : "Opslaan"}
          </Button>
        </div>
      </div>

      {showPreview ? (
        <ConfiguratorPreview
          name={name}
          steps={steps}
          settings={settings}
          onClose={() => setShowPreview(false)}
        />
      ) : (
        <>
          {/* Basic Info */}
          <Card
            className={
              isDark
                ? "border-white/[0.06] bg-white/[0.03]"
                : "border-gray-200/80"
            }
          >
            <CardHeader>
              <CardTitle
                className={`text-lg flex items-center space-x-2 ${isDark ? "text-white" : ""}`}
              >
                <SlidersHorizontal
                  className={`h-5 w-5 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                />
                <span>Basisinformatie</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className={isDark ? "text-gray-300" : ""}>
                  Naam *
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="bv. Fiets Configurator"
                  className={
                    isDark
                      ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600"
                      : "border-gray-200"
                  }
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? "text-gray-300" : ""}>
                  Cover afbeelding
                </Label>
                <input
                  ref={coverImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleCoverImageUpload}
                  className="hidden"
                />
                {image ? (
                  <div className="relative w-fit">
                    <img
                      src={getImageUrl(image)}
                      alt="Cover"
                      className={`h-32 w-full max-w-xs rounded-lg object-cover border ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                    />
                    <button
                      type="button"
                      onClick={() => setImage("")}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => coverImageInputRef.current?.click()}
                    disabled={uploading}
                    className={`h-32 w-full max-w-xs rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-colors ${isDark ? "border-white/10 hover:border-white/20" : "border-gray-300 hover:border-gray-400"}`}
                  >
                    {uploading ? (
                      <RefreshCw className="h-6 w-6 text-gray-400 animate-spin" />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-gray-400" />
                        <span className="text-xs text-gray-400 mt-1">
                          Upload
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="description"
                  className={isDark ? "text-gray-300" : ""}
                >
                  Beschrijving
                </Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Beschrijf kort waarvoor deze configurator dient..."
                  rows={3}
                  className={
                    isDark
                      ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600"
                      : "border-gray-200"
                  }
                />
              </div>
              <div className="flex items-center space-x-4">
                <Badge
                  className={
                    status === "published"
                      ? isDark
                        ? "bg-green-900/30 text-green-400"
                        : "bg-green-100 text-green-700"
                      : isDark
                        ? "bg-amber-900/30 text-amber-400"
                        : "bg-amber-100 text-amber-700"
                  }
                >
                  {status === "published" ? "Gepubliceerd" : "Concept"}
                </Badge>
                {!isNew && (
                  <span
                    className={`text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    ID: {configuratorId}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Steps Builder */}
          <Card
            className={
              isDark
                ? "border-white/[0.06] bg-white/[0.03]"
                : "border-gray-200/80"
            }
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle
                    className={`text-lg flex items-center space-x-2 ${isDark ? "text-white" : ""}`}
                  >
                    <Layers
                      className={`h-5 w-5 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                    />
                    <span>Stappen</span>
                    <Badge
                      variant="secondary"
                      className={`ml-2 ${isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-600"}`}
                    >
                      {steps.length}
                    </Badge>
                  </CardTitle>
                  <CardDescription className={isDark ? "text-gray-500" : ""}>
                    Definieer de stappen die je klant doorloopt. Elke stap bevat
                    opties waaruit gekozen kan worden.
                  </CardDescription>
                </div>
                <Button
                  onClick={openAddStep}
                  className={
                    isDark
                      ? "bg-white text-black hover:bg-gray-200"
                      : "bg-gray-900 text-white hover:bg-gray-800"
                  }
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Stap Toevoegen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {steps.length === 0 ? (
                <div
                  className={`text-center py-12 border-2 border-dashed rounded-lg ${isDark ? "border-white/10" : "border-gray-200"}`}
                >
                  <Layers
                    className={`mx-auto h-12 w-12 mb-3 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                  />
                  <h3
                    className={`font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Nog geen stappen
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Voeg stappen toe zoals "Type", "Model", "Kleur", "Maat",
                    etc.
                  </p>
                  <Button
                    onClick={openAddStep}
                    variant="outline"
                    className={
                      isDark
                        ? "border-white/[0.08] text-gray-300 hover:bg-white/[0.04]"
                        : "border-gray-200 text-gray-600 hover:bg-gray-50"
                    }
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Eerste Stap Toevoegen
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {stepSegments.map((segment, segIdx) => {
                    // â”€â”€ Render a single ungrouped step â”€â”€
                    if (segment.type === "step") {
                      const { step, index } = segment;
                      return (
                        <div
                          key={step.id}
                          className={`border rounded-lg transition-all ${
                            activeStepIndex === index
                              ? isDark
                                ? "border-white/20 shadow-sm"
                                : "border-gray-400 shadow-sm"
                              : isDark
                                ? "border-white/[0.06] hover:border-white/10"
                                : "border-gray-100 hover:border-gray-200"
                          }`}
                        >
                          <StepRow
                            step={step}
                            index={index}
                            activeStepIndex={activeStepIndex}
                            setActiveStepIndex={setActiveStepIndex}
                            isDark={isDark}
                            stepsLength={steps.length}
                            openEditStep={openEditStep}
                            duplicateStep={duplicateStep}
                            handleDeleteStep={handleDeleteStep}
                            moveStep={moveStep}
                            openAddOption={openAddOption}
                            openEditOption={openEditOption}
                            duplicateOption={duplicateOption}
                            handleDeleteOption={handleDeleteOption}
                            moveOption={moveOption}
                            setDefaultOption={setDefaultOption}
                          />
                        </div>
                      );
                    }

                    // â”€â”€ Render a group folder â”€â”€
                    const isExpanded = expandedGroups.has(segment.name);
                    return (
                      <div
                        key={`group-${segment.name}-${segIdx}`}
                        className={`border rounded-lg overflow-hidden ${isDark ? "border-white/[0.08] bg-white/[0.03]" : "border-gray-200 bg-gray-50/30"}`}
                      >
                        {/* Group header */}
                        <button
                          onClick={() => toggleGroup(segment.name)}
                          className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${isDark ? "hover:bg-white/[0.04]" : "hover:bg-gray-50/60"}`}
                        >
                          <div className="flex items-center space-x-2.5">
                            <div
                              className={`h-8 w-8 rounded-lg flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                            >
                              <FolderOpen
                                className={`h-4 w-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                              />
                            </div>
                            <div className="text-left">
                              <h3
                                className={`font-semibold text-sm ${isDark ? "text-white" : "text-gray-900"}`}
                              >
                                {segment.name}
                              </h3>
                              <p
                                className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {segment.items.length} stap
                                {segment.items.length !== 1 ? "pen" : ""}
                              </p>
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          )}
                        </button>

                        {/* Grouped steps */}
                        {isExpanded && (
                          <div
                            className={`border-t ${isDark ? "border-white/[0.08] bg-white/[0.03]" : "border-gray-200 bg-white"}`}
                          >
                            <div className="p-2 space-y-2">
                              {segment.items.map(({ step, index }) => (
                                <div
                                  key={step.id}
                                  className={`border rounded-lg transition-all ${
                                    activeStepIndex === index
                                      ? isDark
                                        ? "border-white/20 shadow-sm"
                                        : "border-gray-400 shadow-sm"
                                      : isDark
                                        ? "border-white/[0.06] hover:border-white/10"
                                        : "border-gray-100 hover:border-gray-200"
                                  }`}
                                >
                                  <StepRow
                                    step={step}
                                    index={index}
                                    activeStepIndex={activeStepIndex}
                                    setActiveStepIndex={setActiveStepIndex}
                                    isDark={isDark}
                                    stepsLength={steps.length}
                                    openEditStep={openEditStep}
                                    duplicateStep={duplicateStep}
                                    handleDeleteStep={handleDeleteStep}
                                    moveStep={moveStep}
                                    openAddOption={openAddOption}
                                    openEditOption={openEditOption}
                                    duplicateOption={duplicateOption}
                                    handleDeleteOption={handleDeleteOption}
                                    moveOption={moveOption}
                                    setDefaultOption={setDefaultOption}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ─── STEP DIALOG ─────────────────────────────────── */}
      <StepDialog
        open={showStepDialog}
        onOpenChange={setShowStepDialog}
        isDark={isDark}
        editingStep={editingStep}
        stepForm={stepForm}
        setStepForm={setStepForm}
        steps={steps}
        onSave={handleSaveStep}
      />

      {/* ─── OPTION DIALOG ────────────────────────────────── */}
      <OptionDialog
        open={showOptionDialog}
        onOpenChange={setShowOptionDialog}
        isDark={isDark}
        editingOption={editingOption}
        optionForm={optionForm}
        setOptionForm={setOptionForm}
        editingOptionStepId={editingOptionStepId}
        steps={steps}
        uploading={uploading}
        onSave={handleSaveOption}
        onMainImageUpload={handleOptionMainImageUpload}
        onExtraImageUpload={handleOptionExtraImageUpload}
        onSvgUpload={handleOptionSvgUpload}
        removeImage={removeImage}
        addDependency={addDependency}
        updateDependency={updateDependency}
        removeDependency={removeDependency}
        getAvailableDependencySteps={getAvailableDependencySteps}
      />

      {/* ─── SETTINGS DIALOG ──────────────────────────────── */}
      <SettingsDialog
        open={showSettingsDialog}
        onOpenChange={setShowSettingsDialog}
        isDark={isDark}
        settings={settings}
        setSettings={setSettings}
      />
    </div>
  );
};

export default ConfiguratorEditor;
